import { useState, useEffect, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  const [tenantId, setTenantId] = useState(() => localStorage.getItem('tenantId') || '');

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('userData');
        const savedTenantId = localStorage.getItem('tenantId') || '';

        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser({ ...parsedUser, tenant_id: parsedUser.tenant_id || savedTenantId });
          setTenantId(savedTenantId);
          setIsAuthenticated(true);

          // Skip token verification for demo tokens
          if (storedToken !== 'demo-token') {
            await verifyToken(storedToken);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Verify token validity
  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });

      if (!response.ok) {
        throw new Error('Token invalid');
      }

      return true;
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
      return false;
    }
  };

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        const { access_token, user: userData } = data;
        const extractedTenantId = userData?.tenant_id || '';

        localStorage.setItem('authToken', access_token);
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('tenantId', extractedTenantId);

        setToken(access_token);
        setUser(userData);
        setTenantId(extractedTenantId);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      } else {
        return { success: false, error: data.detail || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  // Demo login function
  const demoLogin = useCallback((demoData) => {
    const { access_token, user: userData } = demoData;
    const extractedTenantId = userData?.tenant_id || '';

    localStorage.setItem('authToken', access_token);
    localStorage.setItem('userData', JSON.stringify(userData));
    localStorage.setItem('tenantId', extractedTenantId);

    setToken(access_token);
    setUser(userData);
    setTenantId(extractedTenantId);
    setIsAuthenticated(true);

    return { success: true, user: userData };
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tenantId');

    setToken(null);
    setUser(null);
    setTenantId('');
    setIsAuthenticated(false);
    window.location.href = '/';
  }, []);

  // Update user data
  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  // Check if user has access to department
  const hasAccessToDepartment = useCallback((department) => {
    if (user?.role === 'admin') return true;
    return user?.departments?.includes(department) || false;
  }, [user]);

  // Get authorization header
  const getAuthHeader = useCallback(() => {
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, [token]);

  // Authenticated fetch wrapper
  const authFetch = useCallback(async (url, options = {}) => {
    const authOptions = {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': localStorage.getItem('tenantId') || '',
        ...options.headers,
      }
    };

    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const response = await fetch(fullUrl, authOptions);

    // Handle 401 responses
    if (response.status === 401) {
      logout();
      throw new Error('Authentication required');
    }

    return response;
  }, [token, logout]);

  // Refresh token (if backend supports it)
  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        const newToken = data.access_token;

        localStorage.setItem('authToken', newToken);
        setToken(newToken);

        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  }, [getAuthHeader, logout]);

  return {
    user,
    isAuthenticated,
    isLoading,
    token,
    tenantId,
    login,
    demoLogin,
    logout,
    updateUser,
    hasRole,
    hasAccessToDepartment,
    getAuthHeader,
    authFetch,
    refreshToken,
    verifyToken
  };
}

// Hook for role-based access control
export function useRoleAccess(requiredRole, requiredDepartment = null) {
  const { user, hasRole, hasAccessToDepartment } = useAuth();

  const hasAccess = () => {
    if (!user) return false;

    // Check role access
    const roleAccess = hasRole(requiredRole) || hasRole('admin');

    // Check department access if required
    const deptAccess = requiredDepartment ? hasAccessToDepartment(requiredDepartment) : true;

    return roleAccess && deptAccess;
  };

  return {
    hasAccess: hasAccess(),
    user,
    role: user?.role,
    departments: user?.departments || []
  };
}
